import type { ConnectionStatus, ServerMessage } from '@/types/chat';

interface ChatWebSocketOptions {
  wsUrl: string;
  authToken?: string | null;
  maxReconnectAttempts?: number;
  reconnectDelayMs?: number;
  onMessage?: (message: ServerMessage) => void;
  onStatusChange?: (status: ConnectionStatus) => void;
  onPermanentDisconnect?: () => void;
}

export class ChatWebSocket {
  private readonly wsUrl: string;
  private readonly maxReconnectAttempts: number;
  private readonly reconnectDelayMs: number;
  private readonly onMessage?: (message: ServerMessage) => void;
  private readonly onStatusChange?: (status: ConnectionStatus) => void;
  private readonly onPermanentDisconnect?: () => void;

  private authToken: string | null;
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private manuallyClosed = false;

  constructor(options: ChatWebSocketOptions) {
    this.wsUrl = options.wsUrl;
    this.authToken = options.authToken ?? null;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
    this.reconnectDelayMs = options.reconnectDelayMs ?? 1000;
    this.onMessage = options.onMessage;
    this.onStatusChange = options.onStatusChange;
    this.onPermanentDisconnect = options.onPermanentDisconnect;
  }

  connect(): void {
    this.manuallyClosed = false;
    this.clearReconnectTimer();
    this.updateStatus('connecting');

    try {
      this.socket = new WebSocket(this.buildUrl());
      this.socket.onopen = () => this.handleOpen();
      this.socket.onclose = (event) => this.handleClose(event);
      this.socket.onerror = () => this.updateStatus('disconnected');
      this.socket.onmessage = (event) => this.handleMessage(event);
    } catch {
      this.updateStatus('disconnected');
      this.scheduleReconnect();
    }
  }

  disconnect(code = 1000, reason = 'Client disconnected'): void {
    this.manuallyClosed = true;
    this.clearReconnectTimer();
    this.updateStatus('disconnected');

    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      this.socket.close(code, reason);
    }

    this.socket = null;
  }

  setAuthToken(token: string | null): void {
    this.authToken = token;
    if (!this.manuallyClosed) {
      this.reconnect();
    }
  }

  reconnect(): void {
    this.disconnect();
    this.manuallyClosed = false;
    this.reconnectAttempts = 0;
    this.connect();
  }

  private buildUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = new URL(`${protocol}//${window.location.host}${this.wsUrl}`);

    if (this.authToken) {
      url.searchParams.set('token', this.authToken);
    }

    return url.toString();
  }

  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.updateStatus('connected');
  }

  private handleClose(event: CloseEvent): void {
    this.socket = null;
    this.updateStatus('disconnected');

    if (this.manuallyClosed || event.code === 1000) {
      return;
    }

    this.scheduleReconnect();
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data) as ServerMessage;
      this.onMessage?.(message);
    } catch {
      // Ignore malformed frames and keep the connection alive.
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.onPermanentDisconnect?.();
      return;
    }

    this.reconnectAttempts += 1;
    const delay = this.reconnectDelayMs * Math.pow(2, this.reconnectAttempts - 1);
    this.updateStatus('connecting');
    this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private updateStatus(status: ConnectionStatus): void {
    this.onStatusChange?.(status);
  }
}
